import { useEffect, useRef } from "react";

import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputTextarea } from "primereact/inputtextarea";

export interface ChatInputProps {
  input: string;
  streaming: boolean;
  disabled?: boolean;
  pendingSelection?: [number, number] | null;
  onInput: (value: string) => void;
  onSend: (text: string) => void;
  onStop: () => void;
  onSelectionApplied?: () => void;
}

export function ChatInput({
  input,
  streaming,
  disabled = false,
  pendingSelection,
  onInput,
  onSend,
  onStop,
  onSelectionApplied,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const appliedSelectionRef = useRef<[number, number] | null>(null);

  useEffect(() => {
    if (disabled || !pendingSelection || appliedSelectionRef.current === pendingSelection) return;
    const el = textareaRef.current;
    if (!el) return;
    appliedSelectionRef.current = pendingSelection;
    el.focus();
    el.setSelectionRange(pendingSelection[0], pendingSelection[1]);
    onSelectionApplied?.();
  }, [disabled, pendingSelection, input, onSelectionApplied]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!disabled && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend(input);
    }
  };

  return (
    <Card
      className="border border-surface-300"
      pt={{ body: { className: "p-0" }, content: { className: "p-0" } }}
    >
      <div className="relative">
        <InputTextarea
          ref={textareaRef}
          value={input}
          onChange={e => onInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="What can I help you discover?"
          disabled={disabled || streaming}
          rows={1}
          maxLength={4000}
          autoResize
          className="w-full bg-transparent border-none outline-none shadow-none px-4 py-3 pr-16 text-sm leading-relaxed block placeholder:text-surface-400"
          aria-label="Message input"
          data-testid="aspen-chat-input"
        />
        <div className="absolute right-2 inset-y-0 flex items-center gap-1">
          {streaming ? (
            <Button
              icon="pi pi-stop-circle"
              rounded
              severity="danger"
              onClick={onStop}
              aria-label="Stop generation"
              data-testid="aspen-chat-stop"
            />
          ) : (
            <Button
              icon="pi pi-send"
              rounded
              onClick={() => onSend(input)}
              disabled={disabled || !input.trim()}
              aria-label="Send message"
              data-testid="aspen-chat-send"
            />
          )}
        </div>
      </div>
    </Card>
  );
}
